import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Download, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../lib/api';
import ClientProfileModal from '../components/ClientProfileModal';

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const getNow = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const SesionesPage = () => {
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [profileClient, setProfileClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientSearchForm, setClientSearchForm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [newSession, setNewSession] = useState({ client_id: '', date: getToday(), time: getNow(), minutes: 8, cabin: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const fileInputRef = useRef(null);

  const fetchSessionsAndClients = async () => {
    try {
      const [sessionsResponse, clientsResponse, cabinsResponse] = await Promise.all([
        api.get('/sessions'),
        api.get('/clients'),
        api.get('/cabins')
      ]);
      
      const sortedSessions = [...(sessionsResponse.data || [])].sort((a, b) => {
        const dateA = a.date || "";
        const dateB = b.date || "";
        const dateCompare = dateB.localeCompare(dateA);
        if (dateCompare !== 0) return dateCompare;
        return (b.time || "").localeCompare(a.time || "");
      });

      setSessions(sortedSessions);
      setClients(clientsResponse.data || []);
      
      // Robust cabin loading
      if (cabinsResponse.data && cabinsResponse.data.length > 0) {
        setCabins(cabinsResponse.data);
      } else {
        setTimeout(async () => {
          try {
            const retryRes = await api.get('/cabins');
            if (retryRes.data) setCabins(retryRes.data);
          } catch(e) {}
        }, 1000);
      }

      if (profileClient) {
        const updated = clientsResponse.data.find(c => c.id === profileClient.id);
        if (updated) setProfileClient(updated);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcelSessions = () => {
    const dataObj = sessions.map(s => {
      const clientObj = clients.find(c => c.id === s.client_id);
      return {
        'Cliente ID': s.client_id || '',
        'Nombre Cliente': clientObj ? clientObj.name : 'Desconocido',
        'Minutos Descontados': s.minutes || 0,
        'Cabina': s.cabin || '',
        'Fecha': formatDate(s.date) || '',
        'Hora': s.time || ''
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataObj);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sesiones");
    XLSX.writeFile(workbook, "Sesiones_SolPlanet.xlsx");
  };

  useEffect(() => {
    fetchSessionsAndClients();
  }, []);

  const handleImportExcelSessions = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm("⚠️ ALERTA ⚠️: ¿Deseas BORRAR TODO el historial de sesiones actual e importarlo de nuevo desde el Excel?")) {
      e.target.value = null;
      return;
    }

    setLoading(true);
    try {
      const normalizeText = (text) => {
        if (!text) return "";
        return String(text)
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim();
      };

      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const bstr = evt.target.result;
          const wb = XLSX.read(bstr, { type: 'array' });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const data = XLSX.utils.sheet_to_json(ws);

          if (data.length === 0) {
            alert("El archivo parece vacío.");
            setLoading(false);
            return;
          }

          await api.delete('/sessions/action/wipe_all');
          let success = 0;
          let failedMatch = 0;
          let detectedKeys = data.length > 0 ? Object.keys(data[0]) : [];

          for (let rawRow of data) {
            const row = {};
            for (let k in rawRow) row[k.trim().toLowerCase()] = rawRow[k];

            const clientNameOrId = row["id_cliente"] || row["id"] || row["client_id"] || row["cliente id"] || row["usuario id"] || row["cliente"] || row["nombre"] || row["nombre cliente"] || row["usuario"] || row["socio"] || row["descripcion"] || row["email"];
            const normalizedTarget = normalizeText(clientNameOrId);

            if (!normalizedTarget) {
              console.warn("Fila sin identificador de cliente (nombre o ID vacíos):", rawRow);
              failedMatch++;
              continue;
            }

            // 1. Try direct ID match (the 22-char Glide ID)
            let client = clients.find(c => c.id === String(clientNameOrId).trim());

            // 2. Fallback to fuzzy matching if no direct ID match
            if (!client) {
              client = clients.find(c => {
                const nC = normalizeText(c.name);
                const nE = normalizeText(c.email);
                return nC === normalizedTarget ||
                  (nE && nE === normalizedTarget) ||
                  nC.includes(normalizedTarget) ||
                  normalizedTarget.includes(nC);
              });
            }

            if (!client) {
              console.warn("No se encontró cliente para la fila:", rawRow);
              failedMatch++;
              continue;
            }

            let rawDate = row["fecha"] || row["date"] || row["fecha sesion"] || row["fecha de sesion"] || row["createddate"];
            if (typeof rawDate === "number") {
              let d = new Date(Math.round((rawDate - 25569) * 86400 * 1000));
              rawDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            } else if (typeof rawDate === "string") {
              const parts = rawDate.split(/[/.-]/);
              if (parts.length === 3) {
                if (parts[0].length === 4) rawDate = `${parts[0]}-${parts[1]}-${parts[2]}`; // YYYY-MM-DD
                else rawDate = `${parts[2]}-${parts[1]}-${parts[0]}`; // DD/MM/YYYY
              }
            }

            let rawTime = row["hora"] || row["time"] || "12:00";
            if (typeof rawTime === "number") {
              const totalSeconds = Math.round(rawTime * 24 * 3600);
              const h = Math.floor(totalSeconds / 3600);
              const m = Math.floor((totalSeconds % 3600) / 60);
              rawTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            }

            const sessionData = {
              client_id: client.id,
              minutes: parseInt(String(row["minutos"] || row["tiempo"] || row["minutos descontados"] || 8).replace(/[^\d]/g, '')),
              date: rawDate,
              time: String(rawTime).substring(0, 5),
              cabin: String(row["cabina"] || row["cabin"] || "")
            };

            try {
              await api.post('/sessions?silent=true', sessionData);
              success++;
            } catch (err) { console.error("Row import error", err); }
          }

          fetchSessionsAndClients();
          if (success === 0 && data.length > 0) {
            alert(`IMPORTACIÓN FALLIDA: 0 sesiones cargadas.\n\nNo he encontrado ningún cliente que coincida. Asegúrate de que la columna con los nombres se llame "Cliente" o "Nombre".\n\nColumnas detectadas en tu Excel: ${detectedKeys.join(", ")}`);
          } else {
            alert(`Importación finalizada.\n- Éxito: ${success} sesiones.\n- Error: ${failedMatch} filas (cliente no encontrado).`);
          }
        } catch (err) {
          console.error(err);
          alert("Error técnico al procesar el Excel.");
          setLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
    e.target.value = null;
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (!newSession.client_id || !newSession.date || !newSession.time || !newSession.minutes || !newSession.cabin) {
      alert("Por favor, rellena todos los campos obligatorios.");
      return;
    }
    try {
      // Optimistic update for minutes in the clients list
      const updatedClients = clients.map(c => {
        if (c.id === newSession.client_id) {
          return { ...c, remaining_minutes: c.remaining_minutes - newSession.minutes };
        }
        return c;
      });
      setClients(updatedClients);

      api.post('/sessions', newSession).then(response => {
        setSessions([response.data, ...sessions].sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time)));
        fetchSessionsAndClients(); // Final sync
      }).catch(error => {
        // Rollback clients on error
        fetchSessionsAndClients();
        alert("Error al crear sesión. Verifique si el cliente tiene suficientes minutos.");
      });

      setShowCreateModal(false);
      setNewSession({ client_id: '', date: getToday(), time: getNow(), minutes: 8, cabin: '' });
      setClientSearchForm('');
    } catch (error) {
      console.error("Error in session creation flow", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const filteredSessions = sessions.filter(session => {
    if (!searchQuery) return true;
    const client = clients.find(c => c.id === session.client_id);
    if (!client) return false;
    const searchLower = searchQuery.toLowerCase();
    return (
      (client.name && client.name.toLowerCase().includes(searchLower)) ||
      (client.dni && client.dni.toLowerCase().includes(searchLower)) ||
      (client.email && client.email.toLowerCase().includes(searchLower))
    );
  });

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const paginatedSessions = filteredSessions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-800 tracking-tight">Sesiones</h1>
          <p className="text-stone-500">Historial completo de sesiones de bronceado</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <Search className="absolute left-3 top-2.5 text-stone-400" size={18} />
          </div>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleImportExcelSessions}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
            title="Sustituir historial de sesiones"
          >
            <Upload size={18} />
            Importar
          </button>
          <button
            onClick={exportToExcelSessions}
            className="flex items-center gap-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
            title="Descargar listado en Excel (XLSX)"
          >
            <Download size={18} />
            Excel
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-stone-900 px-4 py-2 rounded-xl font-medium shadow-sm shadow-amber-500/20 transition-colors"
          >
            Registrar Sesión
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone-500">Cargando sesiones...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 text-stone-500 border-b border-stone-100">
                  <th className="px-6 py-4 font-medium">Cliente</th>
                  <th className="px-6 py-4 font-medium">Cabina</th>
                  <th className="px-6 py-4 font-medium">Tiempo</th>
                  <th className="px-6 py-4 font-medium">Fecha</th>
                  <th className="px-6 py-4 font-medium">Hora</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSessions.map((session) => {
                  const clientObj = clients.find(c => c.id === session.client_id);
                  return (
                    <tr
                      key={session.id}
                      onClick={() => { if (clientObj) setProfileClient(clientObj); }}
                      className={`border-b border-stone-50 transition-colors ${clientObj ? 'hover:bg-stone-50/50 cursor-pointer' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-stone-800">
                          {clients.find(c => c.id === session.client_id)?.name || 'Cliente Eliminado'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-stone-600">
                        {session.cabin || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                          {session.minutes} min
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-stone-800">{formatDate(session.date)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-stone-800">{session.time}</div>
                      </td>
                    </tr>
                  );
                })}
                {filteredSessions.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                      No hay sesiones registradas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-stone-100 bg-stone-50/50 gap-4">
                <div className="text-sm text-stone-500">
                  Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredSessions.length)}</span> de <span className="font-medium">{filteredSessions.length}</span> resultados
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white shadow-sm"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm text-stone-600 font-medium px-2">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white shadow-sm"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-stone-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-semibold text-stone-800">Registrar Sesión</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-stone-400 hover:text-stone-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateSession} className="p-6 space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-stone-700 mb-1">Cliente *</label>
                <input
                  type="text"
                  placeholder="Buscar por nombre..."
                  value={clientSearchForm}
                  onChange={(e) => {
                    setClientSearchForm(e.target.value);
                    setShowClientDropdown(true);
                    setNewSession({ ...newSession, client_id: '' });
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                  required={!newSession.client_id}
                />

                {showClientDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowClientDropdown(false)}></div>
                    <div className="absolute z-20 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-60 overflow-y-auto custom-scrollbar">
                      {clients.filter(c => c.name.toLowerCase().includes(clientSearchForm.toLowerCase())).map(c => (
                        <div
                          key={c.id}
                          className="px-4 py-2 hover:bg-stone-50 cursor-pointer border-b border-stone-50 last:border-0"
                          onClick={() => {
                            setNewSession({ ...newSession, client_id: c.id });
                            setClientSearchForm(`${c.name}`);
                            setShowClientDropdown(false);
                          }}
                        >
                          <div className="font-medium text-stone-800">{c.name}</div>
                          <div className="text-xs text-stone-500">{c.remaining_minutes} min disponibles</div>
                        </div>
                      ))}
                      {clients.filter(c => c.name.toLowerCase().includes(clientSearchForm.toLowerCase())).length === 0 && (
                        <div className="px-4 py-3 text-sm text-stone-500 text-center">No se encontraron clientes</div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Fecha *</label>
                  <input
                    type="date"
                    value={newSession.date}
                    onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                    className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Hora *</label>
                  <input
                    type="time"
                    value={newSession.time}
                    onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                    className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Minutos *</label>
                  <input
                    type="number"
                    min="1"
                    value={newSession.minutes}
                    onChange={(e) => setNewSession({ ...newSession, minutes: parseInt(e.target.value) || 0 })}
                    className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Cabina *</label>
                  <select
                    value={newSession.cabin}
                    onChange={(e) => setNewSession({ ...newSession, cabin: e.target.value })}
                    className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                    required
                  >
                    <option value="" disabled>Selecciona una cabina</option>
                    {cabins.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-stone-500 hover:text-stone-700 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-amber-500 text-stone-900 px-4 py-2 rounded-xl font-medium hover:bg-amber-600 transition"
                >
                  Registrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {profileClient && (
        <ClientProfileModal
          client={profileClient}
          onClose={() => setProfileClient(null)}
          onUpdate={fetchSessionsAndClients}
        />
      )}
    </div>
  );
};

export default SesionesPage;
